import React, { useEffect, useMemo, useState } from "react";
import { appsDaCentral } from "../../lib/appsDaCentral";
import { carregarPessoas, chamarSlack, EstadoSlack, MENSAGEM_ERRO_SLACK, PessoaSlack } from "./slackPainel";

// Aba "Responsáveis" (só admins): quem o "Avisar no Slack" marca em cada app. O padrão vale para
// todo app sem responsável próprio. O servidor também confere o cargo (api/slack.ts).
const AdminResponsaveis: React.FC<{ slack: EstadoSlack | null; aoMudar: () => void }> = ({ slack, aoMudar }) => {
  const [pessoas, setPessoas] = useState<PessoaSlack[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const apps = useMemo(() => appsDaCentral("pt"), []);

  useEffect(() => {
    if (!slack?.configurado) return;
    carregarPessoas()
      .then(setPessoas)
      .catch(() => setErro("Não consegui carregar as pessoas do Slack."));
  }, [slack?.configurado]);

  if (!slack) return <p className="metricas-vazio">Carregando…</p>;
  if (!slack.configurado) {
    return (
      <section className="metricas-bloco">
        <h2>Responsáveis no Slack</h2>
        <p className="metricas-legenda">O Slack ainda não está configurado neste ambiente.</p>
      </section>
    );
  }

  const padrao = slack.responsaveis.find((r) => r.app === "*");
  const proprio = (slugs: string[]) => slack.responsaveis.find((r) => slugs.includes(r.app));

  const salvar = async (chave: string, slugs: string[], slackId: string | null) => {
    setErro(null);
    setSalvando(chave);
    try {
      const r = await chamarSlack({ acao: "responsavel", apps: slugs, slackId });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) setErro(MENSAGEM_ERRO_SLACK[json?.erro] || "Não consegui salvar. Tente de novo.");
      else aoMudar();
    } finally {
      setSalvando(null);
    }
  };

  const opcoes = (atual: string | undefined) =>
    pessoas ? (
      pessoas.map((p) => (
        <option key={p.id} value={p.id}>
          {p.nome}
        </option>
      ))
    ) : (
      <option value={atual || ""}>Carregando…</option>
    );

  return (
    <section className="metricas-bloco">
      <h2>Responsáveis no Slack</h2>
      <p className="metricas-legenda">
        Quem o "Avisar no Slack" marca em cada app. Apps sem responsável próprio ficam com o padrão.
      </p>
      {erro && <p className="metricas-erro">{erro}</p>}
      <table className="metricas-tabela responsaveis-tabela">
        <tbody>
          <tr className="responsaveis-padrao">
            <td>
              <strong>Padrão</strong>
              <small>todos os apps sem responsável próprio</small>
            </td>
            <td>
              <select
                value={padrao?.slack_id || ""}
                disabled={!pessoas || salvando === "*"}
                onChange={(e) => salvar("*", ["*"], e.target.value)}
              >
                {opcoes(padrao?.slack_id)}
              </select>
            </td>
          </tr>
          {apps.map((app) => {
            const slugs = Array.from(new Set([app.slug, app.sluges]));
            const atual = proprio(slugs);
            return (
              <tr key={app.slugamigavel}>
                <td>{app.name}</td>
                <td>
                  <select
                    value={atual?.slack_id || ""}
                    disabled={!pessoas || salvando === app.slug}
                    onChange={(e) => salvar(app.slug, slugs, e.target.value || null)}
                  >
                    <option value="">Padrão ({padrao?.nome.split(" ")[0] || "—"})</option>
                    {opcoes(atual?.slack_id)}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};

export default AdminResponsaveis;
